import React from 'react';
import { useLanguage } from '@/state/language/LanguageProvider';
import { GapRadarChart } from '@/components/assessment-engine/GapRadarChart';
import { GapValue } from '@/components/assessment-engine/GapValue';
import { QuestionScoresTable } from '@/components/assessment-engine/QuestionScoresTable';
import { ResultsComparisonLayout } from '@/components/assessment-engine/ResultsComparisonLayout';
import { ResultsStrengthsWeaknesses } from '@/components/assessment-engine/ResultsStrengthsWeaknesses';
import {
  formatScore,
  scaleSigma,
  scoreGapFromTarget,
} from '@/components/assessment-engine/resultsScoreUtils';
import {
  AE_MUTED,
  AE_SECTION_TITLE,
} from '@/components/assessment-engine/layout/aeTheme';
import type { AeInstantResults, AeSectionScore } from '@/types/assessmentEngine';
import type { AeMaturityPayload } from '@/components/assessment-engine/template-builder/maturityMapUtils';
import { ResultsMaturityStrip } from '@/components/assessment-engine/ResultsMaturityStrip';

export type AssessmentResultsViewModel = {
  scale_max: number;
  sections: Array<AeSectionScore & { gap?: number | null }>;
  questions?: AeInstantResults['questions'];
  scoring_footnote?: string;
  benchmark_b1: AeInstantResults['benchmark_b1'];
  maturity?: AeMaturityPayload | null;
};

interface AssessmentResultsViewProps {
  results: AssessmentResultsViewModel;
  /** Column / legend label for the primary score series. */
  scoreLabel?: string;
  /** Optional subtitle under the title area (caller supplies title). */
  showBenchmarkHint?: boolean;
  /** Use “Overall capability is…” instead of “Your capability is…”. */
  overallCapabilityHeadline?: boolean;
}

/**
 * Shared spider + section table + question scores used by respondent instant
 * results and campaign administrator report (same presentation).
 */
export const AssessmentResultsView: React.FC<AssessmentResultsViewProps> = ({
  results,
  scoreLabel,
  showBenchmarkHint = true,
  overallCapabilityHeadline = false,
}) => {
  const { t } = useLanguage();
  const primaryLabel = scoreLabel ?? t('assessmentEngine.results.yourScore');
  const scaleMax = results.scale_max;
  const maxSigma = scaleSigma(scaleMax);
  const questions = results.questions ?? [];

  const b1 = results.benchmark_b1;
  const showB1 = b1?.cohort_met === true;
  const b1Map: Record<string, number | null> = {};
  const b1Detail: Record<string, { mean: number | null; stdDev?: number | null }> = {};
  if (showB1 && b1) {
    for (const sec of b1.sections) {
      b1Map[sec.section_id] = sec.mean_score;
      b1Detail[sec.section_id] = {
        mean: sec.mean_score,
        stdDev: sec.std_dev ?? null,
      };
    }
  }

  return (
    <div className="space-y-6">
      {showBenchmarkHint && (
        <p className={`${AE_MUTED} text-xs`}>
          {showB1 && b1
            ? t('assessmentEngine.results.b1ResponseCountPeriod', {
                count: b1.cohort_count,
                period: b1.time_period_key || '—',
              })
            : t('assessmentEngine.report.benchmarkHidden')}
        </p>
      )}

      {results.maturity?.enabled && (
        <ResultsMaturityStrip
          maturity={results.maturity}
          sections={results.sections}
          scaleMax={scaleMax}
          overallHeadline={overallCapabilityHeadline}
          showBenchmark={showB1}
        />
      )}

      <ResultsComparisonLayout
        chart={
          <GapRadarChart
            sections={results.sections}
            scaleMax={results.scale_max}
            aggregateBySection={showB1 ? b1Map : undefined}
            yourScoreLabel={primaryLabel}
            aggregateLabel={t('assessmentEngine.report.benchmark')}
          />
        }
        table={
          <>
            <h2 className={`${AE_SECTION_TITLE} mb-3`}>{t('assessmentEngine.report.section')}</h2>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[var(--color-border)] text-left text-[var(--color-surface-text)]">
                  <th className="py-2">{t('assessmentEngine.report.section')}</th>
                  {!results.maturity?.enabled && <th className="py-2">{primaryLabel}</th>}
                  {results.maturity?.enabled && (
                    <th className="py-2">
                      {overallCapabilityHeadline
                        ? t('assessmentEngine.results.overallCapabilityBand')
                        : t('assessmentEngine.results.yourCapabilityBand')}
                    </th>
                  )}
                  {showB1 && !results.maturity?.enabled && (
                    <th className="py-2">{t('assessmentEngine.report.benchmark')}</th>
                  )}
                  {showB1 && results.maturity?.enabled && (
                    <th className="py-2">{t('assessmentEngine.results.peerCapabilityBand')}</th>
                  )}
                  {showB1 && (
                    <th className="py-2">{t('assessmentEngine.results.gapToBenchmark')}</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {results.sections.map((sec) => {
                  const peer = b1?.sections.find((s) => s.section_id === sec.section_id);
                  const gapToBenchmark = scoreGapFromTarget(
                    sec.mean_score,
                    peer?.mean_score ?? null
                  );
                  const benchSigma =
                    peer?.std_dev != null && peer.std_dev > 0 ? peer.std_dev : maxSigma;
                  const matSec = results.maturity?.sections.find(
                    (s) => s.section_id === sec.section_id
                  );
                  const matPeer = results.maturity?.benchmark_b1?.sections.find(
                    (s) => s.section_id === sec.section_id
                  );
                  return (
                    <tr key={sec.section_id} className="border-b border-[var(--color-border)]">
                      <td className="py-2 text-[var(--color-surface-text)]">{sec.section_name}</td>
                      {!results.maturity?.enabled && (
                        <td className="py-2 text-[var(--color-surface-text)]">
                          {formatScore(sec.mean_score)}
                        </td>
                      )}
                      {results.maturity?.enabled && (
                        <td className="py-2 text-[var(--color-surface-text)]">
                          {matSec?.label ?? '—'}
                          {matSec?.mean_score != null && (
                            <span className="block text-xs text-[var(--color-secondary-text)]">
                              {formatScore(matSec.mean_score)} / {scaleMax}
                            </span>
                          )}
                        </td>
                      )}
                      {showB1 && !results.maturity?.enabled && (
                        <td className="py-2 text-[var(--color-surface-text)]">
                          {formatScore(peer?.mean_score)}
                        </td>
                      )}
                      {showB1 && results.maturity?.enabled && (
                        <td className="py-2 text-[var(--color-surface-text)]">
                          {matPeer?.label ?? '—'}
                          {matPeer?.mean_score != null && (
                            <span className="block text-xs text-[var(--color-secondary-text)]">
                              {formatScore(matPeer.mean_score)} / {scaleMax}
                            </span>
                          )}
                        </td>
                      )}
                      {showB1 && (
                        <td className="py-2">
                          <GapValue gap={gapToBenchmark} sigma={benchSigma} />
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {results.scoring_footnote && (
              <p className="mt-3 text-xs text-[var(--color-secondary-text)]">
                {results.scoring_footnote}
              </p>
            )}
          </>
        }
      />

      <ResultsStrengthsWeaknesses
        sections={results.sections}
        questions={questions}
        scaleMax={scaleMax}
        useBenchmark={showB1}
        b1BySectionId={b1Detail}
      />

      <QuestionScoresTable
        questions={questions}
        scaleMax={scaleMax}
        showBenchmark={showB1}
        scoreColumnLabel={primaryLabel}
      />
    </div>
  );
};
